import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { setSentryUser } from '../lib/sentry';
import { setCurrentTeamId } from '../lib/api';
import { queryClient } from '../lib/queryClient';

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
  institutionType?: string;
  isSuperAdmin?: boolean;
  emailVerified?: boolean;
  passwordHash?: string;
  subscriptionStatus: string;
  subscriptionPlan?: 'PROFESSIONAL' | 'CORE' | 'FAIR_LENDING' | 'CRA' | 'ENTERPRISE';
  trialEndsAt?: string;
  currentPeriodEnd?: string;
  stripeCustomerId?: string;
  monthlyScans?: number;
  scanLimit?: number | null;
  createdAt?: string;
  updatedAt?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  setAuth: (user: User, token: string) => void;
  setUser: (user: User) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,

      setAuth: (user, token) => {
        localStorage.setItem('token', token);
        setSentryUser(user);
        set({ user, token, isAuthenticated: true });
      },

      setUser: (user) => {
        setSentryUser(user);
        set({ user });
      },

      logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('active-team-storage');
        setCurrentTeamId(null);
        setSentryUser(null);
        // Drop all cached query data so a different user logging in on the same
        // tab can't briefly see the previous user's tenant data from cache.
        queryClient.clear();
        set({ user: null, token: null, isAuthenticated: false });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
